import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { Box, Button, MenuItem, Paper, Select, SelectChangeEvent, styled, Typography } from "@mui/material";
import Grid from '@mui/material/Grid';
import { OverlayAsset } from '@lib/types/OverlayAsset';
import { PhotoEvent } from 'common-models';
import { FC, FormEvent, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useListOverlaysQuery } from 'src/features/Admin/adminSlice';

interface EventPhotoUploadFormProps {
    photoEvent: PhotoEvent;
    onSubmitFileUpload: (event: FormEvent<HTMLFormElement>) => void;
    onFilesChanged: (event: React.ChangeEvent<HTMLInputElement>) => void;
    overlayKey?: string;
    onOverlayKeyChanged?: (overlayKey: string) => void;
}

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0, 0, 0, 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

const FileDropZone = styled(Paper)(({ theme }) => ({
    border: `2px dashed ${theme.palette.primary.main}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(3),
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    backgroundColor: theme.palette.action.hover,
    '&:hover': {
        backgroundColor: theme.palette.action.selected,
        borderColor: theme.palette.primary.dark,
    },
    '&.drag-over': {
        backgroundColor: theme.palette.primary.light,
        borderColor: theme.palette.primary.main,
        borderStyle: 'solid',
        transform: 'scale(1.02)',
    },
}));

const EventPhotoUploadForm: FC<EventPhotoUploadFormProps> = ({
    photoEvent,
    onSubmitFileUpload,
    onFilesChanged,
    overlayKey,
    onOverlayKeyChanged,
}) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [selectedFiles, setSelectedFiles] = useState<FileList | null>(null);
    const [isDragOver, setIsDragOver] = useState(false);

    const auth = useAuth();
    const { data: overlays = [] } = useListOverlaysQuery(
        { accessToken: auth.user?.access_token ?? '' },
        { skip: !auth.user?.access_token },
    );
    const defaultOverlay = overlays.find((o: OverlayAsset) => o.isDefault);
    const showOverlaySelector = !photoEvent.isExternalCopyright && overlays.length > 0;

    const handleOverlayChange = (event: SelectChangeEvent<string>) => {
        onOverlayKeyChanged?.(event.target.value);
    };

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        handleFilesSelected(event.target.files);
    };

    const handleFilesSelected = (files: FileList | null) => {
        if (files && files.length > 0) {
            // Filter to only image files
            const imageFiles = Array.from(files).filter(file => file.type.startsWith('image/'));

            if (imageFiles.length > 0) {
                // Get existing files for duplicate checking
                const existingFiles = selectedFiles ? Array.from(selectedFiles) : [];

                // Filter out duplicates based on name and size
                const newFiles = imageFiles.filter(newFile => {
                    return !existingFiles.some(existingFile =>
                        existingFile.name === newFile.name &&
                        existingFile.size === newFile.size
                    );
                });

                if (newFiles.length > 0) {
                    // Combine existing files with new non-duplicate files
                    const allFiles = [...existingFiles, ...newFiles];

                    // Create a new FileList-like object with all files
                    const dataTransfer = new DataTransfer();
                    allFiles.forEach(file => dataTransfer.items.add(file));

                    setSelectedFiles(dataTransfer.files);

                    // Create a synthetic event to pass to the parent handler
                    const syntheticEvent = {
                        target: { files: dataTransfer.files }
                    } as React.ChangeEvent<HTMLInputElement>;
                    onFilesChanged(syntheticEvent);
                }
            }
        }
    };

    const handleDropZoneClick = () => {
        fileInputRef.current?.click();
    };

    const handleDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragOver(true);
    };

    const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragOver(false);
    };

    const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
    };

    const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragOver(false);

        const droppedFiles = event.dataTransfer.files;
        handleFilesSelected(droppedFiles);
    };

    const handleFormSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (selectedFiles && selectedFiles.length > 0) {
            // Create a synthetic form event with the selected files
            const syntheticFormEvent = {
                ...event,
                preventDefault: () => { }, // Add the missing preventDefault method
                target: {
                    ...event.target,
                    querySelector: () => ({
                        files: selectedFiles
                    })
                }
            } as FormEvent<HTMLFormElement>;

            onSubmitFileUpload(syntheticFormEvent);
        }

        // Reset selection after upload
        resetSelection();
    };

    const resetSelection = () => {
        setSelectedFiles(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    return (
        <Box className='event-gallery-admin-area-frame'>
            <Typography variant="h2" component="h2" gutterBottom>
                Add Event Photos
            </Typography>

            {/* Overlay Selector - only show when overlay is enabled */}
            {showOverlaySelector && (
                <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>
                        Select overlay image:
                    </Typography>
                    <Select
                        value={overlayKey || defaultOverlay?.key || ''}
                        onChange={handleOverlayChange}
                        displayEmpty
                        sx={{ minWidth: 300 }}
                    >
                        {overlays.map((overlay: OverlayAsset) => (
                            <MenuItem key={overlay.key} value={overlay.key}>
                                {overlay.fileName}
                                {overlay.isDefault && ' (default)'}
                            </MenuItem>
                        ))}
                    </Select>
                </Box>
            )}

            <form onSubmit={handleFormSubmit}>
                <Grid container spacing={2}>
                    <Grid size={12}>
                        <FileDropZone
                            onClick={handleDropZoneClick}
                            onDragEnter={handleDragEnter}
                            onDragLeave={handleDragLeave}
                            onDragOver={handleDragOver}
                            onDrop={handleDrop}
                            elevation={0}
                            className={isDragOver ? 'drag-over' : ''}
                        >
                            <CloudUploadIcon
                                sx={{
                                    fontSize: 48,
                                    color: isDragOver ? 'primary.contrastText' : 'primary.main',
                                    mb: 2
                                }}
                            />
                            <Typography variant="h6" gutterBottom color={isDragOver ? 'primary.contrastText' : 'inherit'}>
                                {isDragOver ? 'Drop files here' : 'Click to select photos'}
                            </Typography>
                            <Typography variant="body2" color={isDragOver ? 'primary.contrastText' : 'text.secondary'}>
                                {isDragOver ? 'Release to upload' : 'or drag and drop your image files here'}
                            </Typography>
                            <Typography variant="caption" display="block" sx={{ mt: 1 }} color={isDragOver ? 'primary.contrastText' : 'text.secondary'}>
                                Supports: JPG, PNG, GIF • Multiple files allowed
                            </Typography>

                            <VisuallyHiddenInput
                                ref={fileInputRef}
                                id='files'
                                type='file'
                                onChange={handleFileSelect}
                                multiple
                                accept="image/jpeg,image/png,image/gif"
                            />
                        </FileDropZone>
                    </Grid>

                    {selectedFiles && selectedFiles.length > 0 && (
                        <Grid size={12}>
                            <Box sx={{
                                p: 2,
                                bgcolor: 'background.paper',
                                borderRadius: 1,
                                border: 1,
                                borderColor: 'divider'
                            }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                    <Typography variant="subtitle2">
                                        Selected Files ({selectedFiles.length}):
                                    </Typography>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        color="error"
                                        onClick={resetSelection}
                                        sx={{ minWidth: 'auto', px: 1 }}
                                    >
                                        Clear
                                    </Button>
                                </Box>
                                {Array.from(selectedFiles).map((file, index) => (
                                    <Typography
                                        key={index}
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ ml: 1 }}
                                    >
                                        • {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
                                    </Typography>
                                ))}
                            </Box>
                        </Grid>
                    )}

                    <Grid size={12}>
                        <Button
                            type="submit"
                            variant='contained'
                            size="large"
                            disabled={!selectedFiles || selectedFiles.length === 0}
                            startIcon={<CloudUploadIcon />}
                            fullWidth
                        >
                            Upload {selectedFiles?.length || 0} Photo{selectedFiles?.length !== 1 ? 's' : ''}
                        </Button>
                    </Grid>
                </Grid>
            </form>
        </Box>
    );
};

export { EventPhotoUploadForm };
